import { basicUniqueId } from '@eigtech/function-utils'
import {
  AppointmentChannel,
  JobAppointment,
  JobAppointmentCompletedEventDetails,
  RecordAppointmentCompletedRequest,
  RecordAppointmentCompletedRequestBody,
  RecordAppointmentCompletedResponse,
} from '@eigtech/restoration-types'
import { GetAppointmentsResponse } from '@eigtech/summarizer-types'
import { contextualPost, useMutation, useQueryClient } from '@eigtech/ui-shared-api'
import { EntityAdminJob } from '../types/EntityAdminJob'
import { AppointmentsQueryKey, jobsQueryKeys, restorationBasePath } from './constants'
import { useOptimisticAddJobTimelineEvent } from './useOptimisticAddJobTimelineEvent'

const recordAppointmentCompleted = ({ jobId, ...payload }: RecordAppointmentCompletedRequest) =>
  contextualPost<RecordAppointmentCompletedResponse, RecordAppointmentCompletedRequestBody>(
    `${restorationBasePath}/job/${jobId}/record-appointment-completed`,
    payload,
    { responseType: 'none' }
  )

export function useRecordAppointmentCompleted(appointmentsQueryKey?: AppointmentsQueryKey) {
  const queryClient = useQueryClient()
  const optimisticAddJobTimelineEvent = useOptimisticAddJobTimelineEvent()

  return useMutation({
    mutationFn: recordAppointmentCompleted,
    async onSuccess(__, { jobId, appointmentId, appointmentNotes }) {
      const queryKey = jobsQueryKeys.detail(jobId)

      await queryClient.cancelQueries({ queryKey })

      const previousJob = queryClient.getQueryData<EntityAdminJob>(queryKey)

      let appointment: JobAppointment | undefined

      if (previousJob) {
        queryClient.setQueryData<EntityAdminJob>(queryKey, () => {
          const appointments = [...(previousJob?.appointments ?? [])]
          const appointmentIndex = (previousJob?.appointments ?? []).findIndex(
            (appointment) => appointment.appointmentId === appointmentId
          )

          if (appointmentIndex >= 0 && appointments[appointmentIndex]) {
            appointments[appointmentIndex].isCompleted = true
            appointments[appointmentIndex].appointmentNotes = appointmentNotes

            appointment = appointments[appointmentIndex]
          }

          return {
            ...previousJob,
            appointments,
          }
        })
      }

      if (appointmentsQueryKey !== undefined) {
        await queryClient.cancelQueries({ queryKey: appointmentsQueryKey })

        const previousAppointments =
          queryClient.getQueryData<GetAppointmentsResponse['appointments']>(appointmentsQueryKey)

        if (previousAppointments) {
          queryClient.setQueryData<GetAppointmentsResponse['appointments']>(
            appointmentsQueryKey,
            () => {
              const appointments = [...previousAppointments]
              const appointmentIndex = previousAppointments.findIndex(
                (appointment) => appointment.appointmentId === appointmentId
              )

              if (appointmentIndex >= 0 && appointments[appointmentIndex]) {
                appointments[appointmentIndex] = {
                  ...appointments[appointmentIndex],
                  isCompleted: true,
                  appointmentNotes,
                }
              }

              return appointments
            }
          )
        }
      }

      await optimisticAddJobTimelineEvent<JobAppointmentCompletedEventDetails>({
        jobId,
        timelineEvent: 'appointmentCompleted',
        point: {
          date: appointment?.appointmentDate ?? new Date().toISOString(),
          details: {
            appointmentChannel: 'Unknown' as AppointmentChannel,
            appointmentDate: new Date().toISOString(),
            appointmentId: `jobappointment_${basicUniqueId()}`,
            appointmentType: 'Unknown',
            participants: [],
            appointmentDescription: '',
            appointmentNotes: '',
            ...appointment,
          },
        },
      })
    },
  })
}
